// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * The module behind each code typed or scanned into a change's module slots (component
 * design §9.5), asked as it is entered — so a module of the wrong type, of another machine,
 * scrapped, fitted elsewhere or missing its own modules is caught while it is still in hand,
 * not by the server's refusal at submit. The server checks all of it again.
 *
 * One lookup per code (`GET /archives/modules/lookup`), debounced like the serial checks so
 * a code typed by hand is not looked up at every prefix.
 */

import { useQueries } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { ApiError } from "@/api/client";
import { api } from "@/api/endpoints";
import type { ModuleLookup } from "@/api/types";
import { normalizeSerial } from "./serials";
import { INSTALLED_LOOKUP_DELAY_MS } from "./useSerialStatus";

export type ModuleLookupState =
  | { status: "checking" }
  | { status: "found"; module: ModuleLookup }
  | { status: "not_found" }
  | { status: "failed"; error: unknown };

/** Normalized code → what the server says about it, for every non-blank code entered. */
export function useModuleLookups(raws: string[]): (raw: string) => ModuleLookupState | null {
  const wanted = useMemo(
    () => [...new Set(raws.map(normalizeSerial).filter(Boolean))].sort(),
    [raws],
  );
  const key = wanted.join("\n");
  const [asked, setAsked] = useState<string[]>([]);
  useEffect(() => {
    const timer = setTimeout(() => setAsked(wanted), INSTALLED_LOOKUP_DELAY_MS);
    return () => clearTimeout(timer);
    // `key` stands for `wanted`: a new array with the same codes must not restart it.
  }, [key]);

  const results = useQueries({
    queries: asked.map((code) => ({
      queryKey: ["module-lookup", code],
      queryFn: () => api.archives.lookupModule(code),
      retry: false,
      staleTime: 0,
    })),
  });

  const states = new Map<string, ModuleLookupState>();
  asked.forEach((code, index) => {
    const result = results[index];
    if (!result || result.isPending) return;
    if (result.data) states.set(code, { status: "found", module: result.data });
    else if (result.error instanceof ApiError && result.error.code === "MODULE_NOT_FOUND") {
      states.set(code, { status: "not_found" });
    } else states.set(code, { status: "failed", error: result.error });
  });
  return (raw: string) => {
    const code = normalizeSerial(raw);
    if (!code) return null;
    return states.get(code) ?? { status: "checking" };
  };
}
